
// client/src/components/media/SchedulePost.jsx
// UNIFIED VERSION - Same platform options and logic as MediaPost, plus scheduling
// UPDATED with TimeSelect and timezone support

import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import AppFooter from "../AppFooter";
import { FaCalendarCheck, FaSpinner, FaArrowLeft, FaEdit } from "react-icons/fa";
import TimeSelect from '../TimeSelect';
import { localToUTC, getNextSlotInTenantTZ } from '../../utils/timezoneUtils';
import PlatformSelector from '../PlatformSelector';
import MediaEditor from './MediaEditor';
import MentionInput from './MentionInput';

// ---- helpers (same as MediaPost) ---------------------------------------------------------------
const isImageUrl = (u = "") => {
  const clean = String(u).split("?")[0];
  const ext = clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
  return ["jpg", "jpeg", "png", "gif", "webp", "heic", "avif"].includes(ext);
};


const detectKind = (urls = []) => {
  const first = (urls[0] || "").toLowerCase().split("?")[0];
  if (/\.(mp4|m4v)$/.test(first)) return "video";
  if (/\.(mov|qt)$/.test(first)) return "mov";
  if (/\.(jpg|jpeg|png|gif|webp|heic|avif)$/.test(first)) return "image";
  return "";
};

const isLocalPublic = (u, base) => {
  try {
    const root = String(base).replace(/\/+$/, "");
    const abs = /^https?:\/\//i.test(String(u)) ? String(u) : `${root}/${String(u).replace(/^\/+/, "")}`;
    const url = new URL(abs);
    return url.pathname.startsWith("/public/");
  } catch {
    return false;
  }
};

export default function SchedulePost() {
  const { tenant, eventId, itemId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const baseUrl =
    process.env.REACT_APP_BASE_URL ||
    window.location.origin ||
    "http://localhost:5000";

  // Normalize to absolute, publicly reachable URL (same as MediaPost)
  const toPublic = (u) => {
    if (!u) return u;
    const root = String(baseUrl).replace(/\/+$/, "");
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("/public/")) return `${root}${u}`;
    if (u.startsWith("/tenants/")) return `${root}/public${u}`;
    const combined = `${root}/${String(u).replace(/^\/+/, "")}`;
    return encodeURI(combined);
  };


  // ---------------- incoming defaults (same as MediaPost) ----------------
  const defaultText =
    state?.content || state?.caption || state?.title || state?.description || "";

  const fromStateImages =
    (Array.isArray(state?.images) && state.images) ||
    (Array.isArray(state?.mediaUrls) && state.mediaUrls) ||
    (Array.isArray(state?.media) && state.media) ||
    (Array.isArray(state?.selectedMedia?.images) && state.selectedMedia.images) ||
    (Array.isArray(state?.media?.images) && state.media.images) ||
    [];

  const fromStateVideos =
    (Array.isArray(state?.videos) && state.videos) ||
    (Array.isArray(state?.selectedMedia?.videos) && state.selectedMedia.videos) ||
    (Array.isArray(state?.media?.videos) && state.media.videos) ||
    [];

  const unique = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

  const imagesRaw = useMemo(() => unique(fromStateImages), [fromStateImages]);
  const videosRaw = useMemo(() => unique(fromStateVideos), [fromStateVideos]);

  const imagesAbsAll = useMemo(() => imagesRaw.map(toPublic), [imagesRaw]);
  const videosAbs = useMemo(() => videosRaw.map(toPublic), [videosRaw]);

  const imagesAbs = useMemo(() => imagesAbsAll.filter(isImageUrl), [imagesAbsAll]);

  // ---------------- local state (same as MediaPost + scheduling) ----------------
  const [content, setContent] = useState(defaultText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Image editor state
  const [editingImageIdx, setEditingImageIdx] = useState(null);
  const [editedImages, setEditedImages] = useState([]);

  // Effective images: use edited replacements where available
  const effectiveImages = useMemo(() =>
    imagesAbs.map((url, i) => editedImages[i] || url),
    [imagesAbs, editedImages]
  );

  const blueskyImages = useMemo(
    () => effectiveImages.filter(isImageUrl).slice(0, 4),
    [effectiveImages]
  );

  // SAME platform toggles as MediaPost
  const [platforms, setPlatforms] = useState({
    instagram: false,
    facebook: false,
    twitter: false,
    linkedin: false,
    bluesky: false,
    youtube: false,
    tiktok: false,
    reddit: false,
  });

  // Reddit-specific state
  const [redditSubreddits, setRedditSubreddits] = useState([]);
  const [redditSubreddit, setRedditSubreddit] = useState('');
  const [redditTitle, setRedditTitle] = useState('');

  // Fetch Reddit subreddits when Reddit is toggled on
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => {
    if (platforms.reddit && redditSubreddits.length === 0) {
      const tenantId = Number(tenant) || 1;
      fetch(`${baseUrl}/api/integrations/reddit/subreddits?tenantId=${tenantId}`)
        .then(r => r.json())
        .then(data => {
          if (data.success && data.items) setRedditSubreddits(data.items);
        })
        .catch(err => console.error('Failed to fetch subreddits:', err));
    }
  }, [platforms.reddit]);

  // NEW: Tenant timezone
  const [tenantTimezone, setTenantTimezone] = useState('America/Chicago');

  // NEW: Scheduling state - separate date and time
  const [scheduleDate, setScheduleDate] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 15);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const [scheduleTime, setScheduleTime] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 15);
    // Round up to next 5-minute increment
    const minutes = d.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 5) * 5;
    if (roundedMinutes === 60) {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0);
    } else {
      d.setMinutes(roundedMinutes);
    }
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${mins}`;
  });

  const selected = useMemo(
    () => Object.entries(platforms).filter(([, v]) => v).map(([k]) => k),
    [platforms]
  );

  // Fetch tenant timezone on mount
  useEffect(() => {
    const fetchTenantTimezone = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/tenants/${tenant}/timezone`,
          { withCredentials: true }
        );
        if (response.data.timezone) {
          setTenantTimezone(response.data.timezone);
          
          // Set default times in tenant timezone
          const nextSlot = getNextSlotInTenantTZ(response.data.timezone, 15);
          setScheduleDate(nextSlot.date);
          setScheduleTime(nextSlot.time);
        }
      } catch (err) {
        console.error('Failed to fetch tenant timezone:', err);
        // Fall back to browser timezone
        const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTenantTimezone(browserTZ);
      }
    };
    
    if (tenant) {
      fetchTenantTimezone();
    }
  }, [tenant, baseUrl]);

  // ---------------- actions (same as MediaPost) ----------------
  const openBlueskyConnect = () => {
    const url = `${baseUrl}/api/integrations/bluesky/login?tenantId=${Number(tenant || 1)}`;
    window.open(url, "bsky-connect", "width=520,height=520,menubar=no,toolbar=no");
  };

  const ensureIgReady = async (tenantId) => {
    const safeFetch = async (url) => {
      try {
        const r = await fetch(url, { credentials: "include" });
        return r.ok ? r.json() : null;
      } catch {
        return null;
      }
    };
    const fbPeek = await safeFetch(`${baseUrl}/api/integrations/facebook/peek?tenantId=${tenantId}`);
    let pages = Array.isArray(fbPeek?.pages) ? fbPeek.pages : [];
    if (!pages.length) {
      const igPeek = await safeFetch(`${baseUrl}/api/integrations/instagram/_peek?tenantId=${tenantId}`);
      pages = Array.isArray(igPeek?.pages) ? igPeek.pages : [];
    }
    const eligible = pages.some((p) => p?.igUserId && (p?.hasPageToken || p?.pageToken));
    if (!eligible) {
      throw new Error(
        "No connected Facebook/IG account for tenant. Go to Integrations → Facebook/Instagram and connect a Page linked to your IG."
      );
    }
  };

  // Handle editor save: MediaEditor uploads internally and returns the new URL
  const handleEditorSaved = (newUrl) => {
    const publicUrl = toPublic(newUrl);
    setEditedImages(prev => {
      const copy = [...prev];
      copy[editingImageIdx] = publicUrl;
      return copy;
    });
    setEditingImageIdx(null);
  };

  // ---------------- MAIN SUBMIT (schedule instead of immediate post) ----------------
  const submit = async () => {
    try {
      setBusy(true);
      setError("");
      setSuccessMessage("");

      // Validation: at least one platform selected
      if (!selected.length) {
        throw new Error("Select at least one platform.");
      }

      // Define tenantId from params
      const tenantId = Number(tenant) || 1;

      // Check platform authorization before scheduling
      try {
        const statusRes = await fetch(
          `${baseUrl}/api/integrations/status/${tenantId}`,
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          if (statusData.success) {
            const statuses = statusData.integrations || {};
            const unauthorized = selected.filter(p => !statuses[p]?.connected);
            if (unauthorized.length) {
              const names = unauthorized.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
              toast.warn(`Please connect to ${names} before scheduling.`);
              navigate(`/${tenant}/integrations`);
              return;
            }
          }
        }
      } catch (err) {
        console.warn('[SchedulePost] Could not verify platform authorization:', err.message);
      }

      // Prepare media URLs (same as MediaPost)
      const mediaUrlsForOthers = unique([...videosAbs, ...effectiveImages]);
      const detected = detectKind(mediaUrlsForOthers);
      const inferredKind = detected === "video" ? "reel" : detected === "image" ? "image" : "";

      // Instagram validations (same as MediaPost)
      if (platforms.instagram) {
        await ensureIgReady(tenantId);

        if (detected === "mov") {
          const first = mediaUrlsForOthers[0] || "";
          if (!isLocalPublic(first, baseUrl)) {
            throw new Error(
              "Instagram only accepts MP4 (H.264 + AAC). Convert your MOV to MP4 or upload it to this app (under /public) so we can auto-convert."
            );
          }
        }

        if (!mediaUrlsForOthers.length && !content.trim()) {
          throw new Error("Instagram requires at least one image/video or a caption.");
        }
      }

      // Reddit validation
      if (platforms.reddit) {
        if (!redditSubreddit) throw new Error("Please select a subreddit for your Reddit post.");
        if (!redditTitle.trim()) throw new Error("Reddit posts require a title.");
      }

      // Prepare Instagram carousel if multiple images
      const igCarousel = effectiveImages.length > 1
        ? effectiveImages.slice(0, 10).map((u) => ({ imageUrl: u }))
        : undefined;

      // Convert tenant-local date/time to UTC MySQL string
      const scheduledUtcMySqlRaw = localToUTC(scheduleDate, scheduleTime, tenantTimezone);

      // Ensure it has seconds (MySQL DATETIME format requires YYYY-MM-DD HH:mm:ss)
      const scheduledUtcMySql =
        /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(scheduledUtcMySqlRaw)
          ? `${scheduledUtcMySqlRaw}:00`
          : scheduledUtcMySqlRaw;

      // Create ISO version for validation
      const scheduledUtcIso = new Date(scheduledUtcMySql.replace(' ', 'T') + 'Z').toISOString();

      // Validate future time
      const now = new Date();
      const scheduledDateTime = new Date(scheduledUtcIso);
      
      if (scheduledDateTime <= now) {
        setError("Scheduled time must be in the future. Please select a later date/time.");
        setBusy(false);
        return;
      }

      // Build payload with both formats for backend compatibility
      const payload = {
        tenantId,
        eventId: eventId ? Number(eventId) : null,
        eventItemId: itemId ? Number(itemId) : null,
        platforms: selected,
        content,
        caption: content,
        mediaUrls: mediaUrlsForOthers,
        kind: inferredKind,
        shareToFeed: true,
        thumbOffsetSec: 0,
        carouselItems: igCarousel,

        // Backend route prefers scheduledAtLocal (MySQL-friendly)
        scheduledAtLocal: scheduledUtcMySql,
        scheduledAtIso: scheduledUtcIso,
        scheduledAt: scheduledUtcIso,

        timezone: tenantTimezone,

        // Reddit-specific fields (stored in provider_payload for the worker)
        ...(platforms.reddit && {
          redditSubreddit,
          redditTitle,
        }),
      };

      console.log("Scheduling post with payload:", payload);

      // Make API call (use relative path for same-origin)
      const response = await axios.post(
        `/api/schedule/schedule`,
        payload,
        { 
          withCredentials: true, 
          timeout: 30000 
        }
      );

      console.log("Schedule response:", response.data);

      if (response.data.success) {
        // Display scheduled time in tenant timezone
        const scheduledDisplay = scheduledDateTime.toLocaleString('en-US', {
          timeZone: tenantTimezone,
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        
        setSuccessMessage(
          `Post scheduled successfully for ${scheduledDisplay}! ` +
          `It will be published to ${selected.join(', ')}.`
        );
        
        // Navigate back after short delay
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        throw new Error(response.data.error || "Failed to schedule post");
      }

    } catch (e) {
      console.error("Error scheduling post:", e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e.message ||
        "Failed to schedule post";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const canSubmit =
    selected.length > 0 &&
    (content.trim().length > 0 || effectiveImages.length > 0 || videosAbs.length > 0);

  // ---------------- render (same as MediaPost + date/time picker) ----------------
  return (
    <div className="container py-3">
      <h4 className="mb-3">Schedule Post</h4>

      {/* Integrations - Dropdown Selector (same as MediaPost) */}
      <div className="mb-3">
        <div className="form-label fw-semibold mb-2">Select Integrations</div>
        <PlatformSelector
          platforms={platforms}
          setPlatforms={setPlatforms}
          onConnect={(platformKey) => {
            if (platformKey === 'bluesky') {
              openBlueskyConnect();
            }
          }}
          showConnectFor={['bluesky']}
        />
      </div>

      {/* Content (with @mention autocomplete) */}
      <div className="mb-3">
        <label className="form-label">Post Content</label>
        <MentionInput
          value={content}
          onChange={setContent}
          rows={5}
          placeholder="Write your post... Type @ to mention a platform handle"
        />
        <div className="form-text">Type @ to mention platform handles (e.g. @nike, @caborojo)</div>
      </div>

      {/* Reddit-specific fields */}
      {platforms.reddit && (
        <div className="mb-3 p-3 rounded" style={{ background: '#fff4f0', border: '1px solid #FF4500' }}>
          <div className="fw-semibold mb-2" style={{ color: '#FF4500' }}>Reddit Post Settings</div>
          <div className="mb-2">
            <label className="form-label small mb-1">Subreddit *</label>
            <select
              className="form-select"
              value={redditSubreddit}
              onChange={(e) => setRedditSubreddit(e.target.value)}
            >
              <option value="">Select a subreddit...</option>
              {redditSubreddits.map((sr) => (
                <option key={sr.name} value={sr.name}>r/{sr.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Post Title *</label>
            <input
              type="text"
              className="form-control"
              value={redditTitle}
              onChange={(e) => setRedditTitle(e.target.value)}
              placeholder="Enter a title for your Reddit post"
              maxLength={300}
            />
            <div className="form-text">{redditTitle.length}/300 characters</div>
          </div>
        </div>
      )}

      {/* UPDATED: Schedule Date & Time with TimeSelect */}
      <div className="mb-3 p-3 rounded" style={{ background: "#f8f9fa", color: "#212529" }}>
        <label className="form-label" style={{ color: "#212529" }}>
          <strong>Schedule Date & Time</strong>
        </label>
        <div className="row">
          <div className="col-md-6 mb-2">
            <label className="form-label small mb-1" style={{ color: "#6c757d" }}>Date</label>
            <input
              type="date"
              className="form-control"
              value={scheduleDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setScheduleDate(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small mb-1" style={{ color: "#6c757d" }}>Time</label>
            <TimeSelect
              value={scheduleTime}
              onChange={setScheduleTime}
              tenantTimezone={tenantTimezone}
              showTimezone={true}
              id="schedule-time"
            />
          </div>
        </div>
        
        {/* Preview of scheduled time in tenant timezone */}
        {scheduleDate && scheduleTime && (
          <div className="alert alert-info mt-3 mb-0">
            📅 Post will be published on{" "}
            {(() => {
              try {
                // Convert to UTC then display in tenant timezone
                const utcMySql = localToUTC(scheduleDate, scheduleTime, tenantTimezone);
                const utcIso = new Date(utcMySql.replace(' ', 'T') + 'Z');
                
                return (
                  <strong>
                    {utcIso.toLocaleString('en-US', {
                      timeZone: tenantTimezone,
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </strong>
                );
              } catch {
                return <strong>{scheduleDate} {scheduleTime}</strong>;
              }
            })()}
          </div>
        )}
      </div>

      {/* Images (with text overlay option) */}
      <div className="mb-3">
        <div className="form-label">Images:</div>

        {effectiveImages.length === 0 ? (
          <div className="text-muted">No images provided.</div>
        ) : (
          <>
            <div
              className="border rounded mb-2"
              style={{
                width: "100%",
                maxWidth: "100%",
                background: "#111",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <img
                src={effectiveImages[0]}
                alt=""
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  objectFit: "contain",
                }}
                onError={(e) => (e.currentTarget.src = "/event.png")}
              />
              <button
                type="button"
                onClick={() => setEditingImageIdx(0)}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: 13,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
                title="Edit image"
              >
                <FaEdit /> Edit
              </button>
            </div>

            {effectiveImages.length > 1 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 10,
                }}
              >
                {effectiveImages.slice(1).map((u, i) => (
                  <div
                    key={i}
                    className="border rounded overflow-hidden"
                    style={{ aspectRatio: "4 / 3", background: "#111", position: "relative" }}
                  >
                    <img
                      src={u}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => (e.currentTarget.src = "/event.png")}
                    />
                    <button
                      type="button"
                      onClick={() => setEditingImageIdx(i + 1)}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "3px 8px",
                        fontSize: 11,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                      title="Edit image"
                    >
                      <FaEdit />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {platforms.bluesky && blueskyImages.length === 0 && !content.trim() && (
          <div className="form-text mt-2">
            Bluesky supports images (no video). Add at least one image or include text.
          </div>
        )}
      </div>

      {/* Text Overlay Editor */}
      {/* Image Editor */}
      {editingImageIdx !== null && effectiveImages[editingImageIdx] && (
        <MediaEditor
          src={effectiveImages[editingImageIdx]}
          tenantId={Number(tenant) || 1}
          eventId={eventId ? Number(eventId) : null}
          onSaved={handleEditorSaved}
          onClose={() => setEditingImageIdx(null)}
        />
      )}

      {/* Videos - SAME as MediaPost */}
      {!!videosAbs.length && (
        <div className="mb-3">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 12,
              marginTop: 8,
            }}
          >
            {videosAbs.map((u, i) => (
              <video key={i} src={u} controls style={{ width: "100%" }} />
            ))}
          </div>
        </div>
      )}

      {/* Error message */}
      {!!error && (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Success message */}
      {!!successMessage && (
        <div className="alert alert-success" role="alert">
          <strong>Success!</strong> {successMessage}
        </div>
      )}

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)} disabled={busy}>
          <FaArrowLeft /> Back
        </button>
        <button
          type="button"
          className="btn-primary-action"
          onClick={submit}
          disabled={busy || !canSubmit}
        >
          {busy ? <FaSpinner className="fa-spin" /> : <FaCalendarCheck />} {busy ? "Scheduling…" : "Schedule Post"}
        </button>
      </div>

      {/* Footer - SAME as MediaPost */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
            disabled: busy,
          },
          {
            icon: busy ? <FaSpinner className="fa-spin" /> : <FaCalendarCheck />,
            label: busy ? "Scheduling…" : "Schedule Post",
            onClick: submit,
            disabled: busy || !canSubmit,
            type: "submit",
          },
        ]}
      />
    </div>
  );
}